import React, { useState, useEffect } from 'react';
import { Team } from '../types/Team';
import { fetchTeamsData, updateTeam } from '../api/apis';
import { TeamDetails } from './TeamDetails';

interface TeamListProps {
  onTeamSelect?: (team: Team) => void;
}

type CategoryFilter = 'All' | 'Male' | 'Female' | 'Mixed';

const formatTime = (dateString: string | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const formatDuration = (duration: { hours: number; minutes: number; seconds: number }): string => {
  return `${duration.hours}:${String(duration.minutes).padStart(2, '0')}:${String(duration.seconds).padStart(2, '0')}`;
};

const formatMembers = (members: { name: string }[]): string => {
  return members.map((m) => m.name).join(', ');
};

export const TeamList: React.FC<TeamListProps> = ({ onTeamSelect }) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('All');

  const loadTeams = async () => {
    setIsLoading(true);
    try {
      const results = await fetchTeamsData(categoryFilter === 'All' ? '' : categoryFilter);
      setTeams(results);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load teams.');
      console.error('Error loading teams:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTeams();
  }, [categoryFilter]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  // If a team is selected, show team details
  if (selectedTeamId) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <button
          onClick={() => {
            setSelectedTeamId(null);
            loadTeams(); // Refresh the team list when going back
          }}
          className="mb-6 flex items-center gap-2 text-blue-500 hover:text-blue-600 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Results
        </button>
        <TeamDetails teamId={selectedTeamId} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Race Results</h1>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value as CategoryFilter)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="All">All Categories</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
          <option value="Mixed">Mixed</option>
        </select>
      </div>
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        <table className="w-full table-fixed">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="w-16 px-6 py-4 text-center text-sm font-semibold text-gray-600">Pos</th>
              <th className="w-1/5 px-6 py-4 text-center text-sm font-semibold text-gray-600">Team</th>
              <th className="w-1/4 px-6 py-4 text-center text-sm font-semibold text-gray-600">Members</th>
              <th className="w-32 px-6 py-4 text-center text-sm font-semibold text-gray-600">Start</th>
              <th className="w-32 px-6 py-4 text-center text-sm font-semibold text-gray-600">Finish</th>
              <th className="w-32 px-6 py-4 text-center text-sm font-semibold text-gray-600">Net Time</th>
            </tr>
          </thead>
          <tbody>
            {teams.map((team, index) => (
              <tr
                key={team.id}
                onClick={() => {
                  setSelectedTeamId(team.id);
                  onTeamSelect?.(team);
                }}
                className="border-b border-gray-100 hover:bg-blue-50 cursor-pointer transition-colors"
              >
                <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">{index + 1}</td>
                <td className="px-6 py-4">
                  <div className="font-medium text-gray-800 truncate">{team.name}</div>
                  <div className="text-sm text-gray-500 truncate">{team.category}</div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 truncate">{formatMembers(team.members)}</td>
                <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">{formatTime(team.raceTime.startTime)}</td>
                <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">{formatTime(team.raceTime.finishTime)}</td>
                <td className="px-6 py-4 font-medium text-gray-800 whitespace-nowrap">{formatDuration(team.netRaceTime)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};